import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tv, CalendarClock, Users2, Sparkles, Pencil, Radio, Layers,
} from "lucide-react";
import { toast } from "sonner";

// TV-shaped tile row that mounts inside ProjectDashboard when the project's
// `project_type` is a TV variant. Non-TV projects never see this component.
//
// Tiles rendered depend on which TV variant:
//   tv_episodic   → Aired-On · Season/Episode
//   tv_daily_soap → Aired-On · Bank Counter · Multi-Unit
//   format_reality → Format Name · Non-Fiction Subtype · Host Talent
export default function TVFormatPanel({ project, onRefresh, onOpenUnits }) {
  const [editOpen, setEditOpen] = useState(false);
  const details = project.format_details || {};
  const ptype = project.project_type;
  const isSoap = ptype === "tv_daily_soap" || ptype === "tv"; // legacy
  const isReality = ptype === "format_reality" || ptype === "format_show";
  const isEpisodic = ptype === "tv_episodic";

  const airedOn = details.aired_on_name || "Not linked";
  const bankBehind = (details.bank_target || 0);
  const currentEp = (details.current_episode_aired || 0);
  const seasonLabel = details.season_number
    ? `Season ${details.season_number}` : "Season —";
  const epsPlanned = details.episodes_planned || "—";

  const units = (project.additional_units || []).filter((u) => !u.deleted_at);

  return (
    <div className="mt-8" data-testid="tv-format-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          TV Format
        </div>
        <button
          onClick={() => setEditOpen(true)}
          className="text-[10px] tracking-[0.22em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="tv-format-edit-btn"
        >
          <Pencil size={11} /> Edit details
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TVTile
          testid="tv-tile-aired-on"
          Icon={Radio}
          color="#0EA5E9"
          topLabel="Aired on"
          bigValue={airedOn}
          bottomLabel={details.telecast_slot || "Slot TBD"}
        />

        {isEpisodic && (
          <TVTile
            testid="tv-tile-season"
            Icon={CalendarClock}
            color="#F5B82E"
            topLabel="Season"
            bigValue={seasonLabel}
            bottomLabel={`${epsPlanned} episodes planned`}
          />
        )}

        {isSoap && (
          <>
            <TVTile
              testid="tv-tile-current-ep"
              Icon={Tv}
              color="#EC4899"
              topLabel="On air"
              bigValue={currentEp ? `Ep ${currentEp}` : "Ep —"}
              bottomLabel={details.running_since
                ? `Since ${details.running_since}` : "Set first-aired date"}
            />
            <TVTile
              testid="tv-tile-bank"
              Icon={Layers}
              color="#10B981"
              topLabel="Bank target"
              bigValue={bankBehind ? `+${bankBehind}` : "Not set"}
              bottomLabel="Episodes ahead of telecast"
            />
            <TVTile
              testid="tv-tile-units"
              Icon={Users2}
              color="#8B5CF6"
              topLabel="Shooting units"
              bigValue={units.length ? `${units.length + 1}` : "1"}
              bottomLabel={units.length
                ? "Tap to manage additional units"
                : "Add Unit B / Chroma"}
              onClick={onOpenUnits}
            />
          </>
        )}

        {isReality && (
          <>
            <TVTile
              testid="tv-tile-format-name"
              Icon={Sparkles}
              color="#F97316"
              topLabel="Format"
              bigValue={details.format_name || "Not set"}
              bottomLabel={details.format_origin
                ? details.format_origin.toUpperCase()
                : "Origin TBD"}
            />
            <TVTile
              testid="tv-tile-subtype"
              Icon={Tv}
              color="#EC4899"
              topLabel="Sub-format"
              bigValue={details.non_fiction_subtype
                ? labelize(details.non_fiction_subtype) : "Not set"}
              bottomLabel={(details.host_talent || []).length
                ? `Host: ${details.host_talent[0]}` : "Add host talent"}
            />
          </>
        )}
      </div>

      <EditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
        onSaved={onRefresh}
      />
    </div>
  );
}

function TVTile({ testid, Icon, color, topLabel, bigValue, bottomLabel, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="cs-card rounded-2xl p-5 text-left relative overflow-hidden group transition-transform hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:cursor-default"
      data-testid={testid}
    >
      <div
        className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-15 blur-2xl pointer-events-none"
        style={{ background: color }}
      />
      <div className="relative flex items-center justify-between">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: `${color}24`, color }}
        >
          <Icon size={17} />
        </div>
        <div className="text-[9px] tracking-[0.24em] uppercase text-white/45">
          {topLabel}
        </div>
      </div>
      <div className="mt-4 font-display text-2xl uppercase tracking-tight text-white leading-tight truncate">
        {bigValue}
      </div>
      <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/45">
        {bottomLabel}
      </div>
    </button>
  );
}

function labelize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function EditDialog({ open, onOpenChange, project, onSaved }) {
  const details = project.format_details || {};
  const [saving, setSaving] = useState(false);
  const [airedOnName, setAiredOnName] = useState(details.aired_on_name || "");
  const [telecastSlot, setTelecastSlot] = useState(details.telecast_slot || "");
  const [seasonNumber, setSeasonNumber] = useState(details.season_number || "");
  const [episodesPlanned, setEpisodesPlanned] = useState(details.episodes_planned || "");
  const [currentEp, setCurrentEp] = useState(details.current_episode_aired || "");
  const [bankTarget, setBankTarget] = useState(details.bank_target || "");
  const [runningSince, setRunningSince] = useState(details.running_since || "");
  const [formatName, setFormatName] = useState(details.format_name || "");
  const [formatOrigin, setFormatOrigin] = useState(details.format_origin || "");
  const [subtype, setSubtype] = useState(details.non_fiction_subtype || "");
  const [hostRaw, setHostRaw] = useState((details.host_talent || []).join(", "));

  const ptype = project.project_type;
  const isSoap = ptype === "tv_daily_soap" || ptype === "tv";
  const isReality = ptype === "format_reality" || ptype === "format_show";
  const isEpisodic = ptype === "tv_episodic";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        aired_on_name: airedOnName || undefined,
        telecast_slot: telecastSlot || undefined,
      };
      if (isEpisodic || isSoap) {
        if (seasonNumber) payload.season_number = Number(seasonNumber);
        if (episodesPlanned) payload.episodes_planned = Number(episodesPlanned);
      }
      if (isSoap) {
        if (currentEp) payload.current_episode_aired = Number(currentEp);
        if (bankTarget) payload.bank_target = Number(bankTarget);
        if (runningSince) payload.running_since = runningSince;
      }
      if (isReality) {
        if (formatName) payload.format_name = formatName;
        if (formatOrigin) payload.format_origin = formatOrigin;
        if (subtype) payload.non_fiction_subtype = subtype;
        const hosts = hostRaw.split(",").map((s) => s.trim()).filter(Boolean);
        if (hosts.length) payload.host_talent = hosts;
      }
      await api.patch(`/projects/${project.id}/format-details`, payload);
      toast.success("Format details saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="tv-format-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-xl">
            Edit TV Format Details
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            All fields are optional — fill them at your own pace as the show progresses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <FieldRow label="Aired on (Channel / OTT)" testid="tv-fld-aired-on">
            <Input value={airedOnName} onChange={(e) => setAiredOnName(e.target.value)}
              placeholder="Star Plus · Netflix · JioCinema"
              className="h-11 bg-black/40 border-white/15" />
          </FieldRow>
          <FieldRow label="Telecast slot" testid="tv-fld-slot">
            <Input value={telecastSlot} onChange={(e) => setTelecastSlot(e.target.value)}
              placeholder="Prime time 9pm · Weekend afternoon"
              className="h-11 bg-black/40 border-white/15" />
          </FieldRow>

          {(isEpisodic || isSoap) && (
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Season #" testid="tv-fld-season">
                <Input type="number" value={seasonNumber}
                  onChange={(e) => setSeasonNumber(e.target.value)}
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
              <FieldRow label="Episodes planned" testid="tv-fld-eps-planned">
                <Input type="number" value={episodesPlanned}
                  onChange={(e) => setEpisodesPlanned(e.target.value)}
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
            </div>
          )}

          {isSoap && (
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Current episode on air" testid="tv-fld-current-ep">
                <Input type="number" value={currentEp}
                  onChange={(e) => setCurrentEp(e.target.value)}
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
              <FieldRow label="Bank target (episodes ahead)" testid="tv-fld-bank">
                <Input type="number" value={bankTarget}
                  onChange={(e) => setBankTarget(e.target.value)}
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
              <FieldRow label="First aired on" testid="tv-fld-running-since">
                <Input type="date" value={runningSince}
                  onChange={(e) => setRunningSince(e.target.value)}
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
            </div>
          )}

          {isReality && (
            <>
              <FieldRow label="Format name" testid="tv-fld-format-name">
                <Input value={formatName} onChange={(e) => setFormatName(e.target.value)}
                  placeholder="Bigg Boss · MasterChef · KBC"
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Format origin" testid="tv-fld-format-origin">
                  <Select value={formatOrigin} onValueChange={setFormatOrigin}>
                    <SelectTrigger className="h-11 bg-black/40 border-white/15">
                      <SelectValue placeholder="Pick one" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                      <SelectItem value="original">Original</SelectItem>
                      <SelectItem value="licensed">Licensed (Endemol · Fremantle)</SelectItem>
                      <SelectItem value="adaptation">Adaptation</SelectItem>
                      <SelectItem value="remake">Remake</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Sub-format" testid="tv-fld-subtype">
                  <Select value={subtype} onValueChange={setSubtype}>
                    <SelectTrigger className="h-11 bg-black/40 border-white/15">
                      <SelectValue placeholder="Pick one" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                      <SelectItem value="reality">Reality</SelectItem>
                      <SelectItem value="talent">Talent</SelectItem>
                      <SelectItem value="quiz">Quiz</SelectItem>
                      <SelectItem value="docu">Documentary</SelectItem>
                      <SelectItem value="talk_show">Talk show</SelectItem>
                      <SelectItem value="cooking">Cooking</SelectItem>
                      <SelectItem value="travel">Travel</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
              </div>
              <FieldRow label="Host talent (comma separated)" testid="tv-fld-host-talent">
                <Input value={hostRaw} onChange={(e) => setHostRaw(e.target.value)}
                  placeholder="Amitabh Bachchan · Salman Khan"
                  className="h-11 bg-black/40 border-white/15" />
              </FieldRow>
            </>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="tv-format-cancel">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="tv-format-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
